import { isNil } from '@flowio/is';
import postPaymentMethodData from '../../services/paymentRequest/postPaymentMethodData';
import { DomReference } from '../../types';
import getSession from '../../utilities/getSession';
import { hideLoadingSpinner, showLoadingSpinner } from '../../utilities/loadingSpinner';
import loadScript from '../../utilities/loadScript';
import { resolveElement } from '../../utilities/elementHelpers';
import isInternalPaymentRequest from '../../validators/isInternalPaymentRequest';
import getAdyenLocale from './getAdyenLocale';

let checkout: Adyen.CheckoutInstance;

type ChallengeShopperParams = {
  challengeToken: string;
  pr: io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest;
  mountContainer: DomReference;
};

type ContainerType = 'threeds2-identify' | 'threeds2-challenge';

function getContainer<T extends Element = HTMLElement>(
  mountContainer: string | T,
  type: ContainerType,
): HTMLDivElement {
  const container = resolveElement<T>(mountContainer);
  const subContainer = container.querySelector<HTMLDivElement>(`.${type}`);
  if (isNil(subContainer)) {
    const newSubContainer = document.createElement('div');
    newSubContainer.classList.add(type);
    container.appendChild(newSubContainer);
    return newSubContainer;
  }
  return subContainer;
}

function challengeShopper(
  {
    challengeToken,
    pr,
    mountContainer,
  }: ChallengeShopperParams,
): Promise<io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest> {
  return new Promise((resolve, reject) => {
    checkout.create('threeDS2Challenge', {
      challengeToken,
      size: '03',
      onComplete: (challengeData: Adyen.ThreeDS2ChallengeResult) => {
        showLoadingSpinner({ container: mountContainer });
        postPaymentMethodData(
          pr,
          {
            type: 'complete_authorization_card',
            action_result: {
              type: 'adyen_v3',
              result: challengeData.data.details,
            },
          },
        ).then((updatedPr) => {
          hideLoadingSpinner();
          resolve(updatedPr);
        }).catch(reject);
      },
      onError: (error) => {
        reject(error);
      },
    }).mount(getContainer(mountContainer, 'threeds2-challenge'));
  });
}

type IdentifyShopperParams = {
  fingerprintToken: string;
  pr: io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest;
  mountContainer: DomReference;
};

function identifyShopper({
  fingerprintToken,
  pr,
  mountContainer,
}: IdentifyShopperParams):
  Promise<io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest> {
  showLoadingSpinner({ container: mountContainer });
  return new Promise((resolve, reject) => {
    checkout.create('threeDS2DeviceFingerprint', {
      fingerprintToken,
      onComplete: (
        fingerprintData: Adyen.ThreeDS2DeviceFingerprintResult,
      ) => {
        postPaymentMethodData(
          pr,
          {
            type: 'complete_authorization_card',
            action_result: {
              type: 'adyen_v3',
              result: fingerprintData.data.details,
            },
          },
        ).then((updatedPr) => {
          resolve(updatedPr);
          hideLoadingSpinner();
        }).catch(reject);
      },
      onError: (error) => {
        reject(error);
        hideLoadingSpinner();
      },
    }).mount(getContainer(mountContainer, 'threeds2-identify'));
  });
}

export default function handleAdyenV3Action(
  pr: io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest,
  mountContainer: DomReference,
): Promise<io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest> {
  const action = isInternalPaymentRequest(pr) ? pr.actual_action : pr.next_action;
  if (isNil(action) || action.type !== 'use_sdk_adyen_v3') {
    throw new Error('handleAdyenV3Action: method inappropriately invoked.');
  }

  return Promise.all([
    loadScript('https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/3.1.0/adyen.js'),
    getSession(),
  ]).then((result) => {
    const session = result[1];
    const adyenEnv = session.environment === 'production' ? 'live' : 'test';
    const adyenLocale = getAdyenLocale();
    if (!checkout) {
      checkout = new AdyenCheckout({
        environment: adyenEnv,
        locale: adyenLocale,
        originKey: action.origin_key,
      });
    }

    switch (action.authentication_token.type) {
      case 'token':
        return challengeShopper({
          challengeToken: action.authentication_token.challenge_token,
          pr,
          mountContainer,
        });
      case 'fingerprint':
        return identifyShopper({
          fingerprintToken: action.authentication_token.fingerprint_token,
          pr,
          mountContainer,
        });
      default:
    }
    return pr;
  });
}
